import { NavLink, Outlet, useLocation, useNavigationType, Link } from "react-router-dom";
import { Suspense, useEffect, useRef, useState } from "react";
import { CalendarDays, ChartNoAxesColumnIncreasing, Landmark, Layers3, ListChecks, RefreshCw, Settings, ChevronDown, MessageCircle } from "lucide-react";
import { useTriggerSync } from "@/hooks/useSync";
import { PageErrorBoundary } from "./PageErrorBoundary";
import "../styles/money.css";

const titles: Record<string, string> = {
  "/assistant": "Talk to Findash", "/": "Calendar", "/spending": "Spending", "/agent": "Daily brief", "/net-worth": "Net worth",
  "/transactions": "Transactions", "/debts": "Debt", "/debts/calendar": "Loan calendar",
  "/debts/runbook": "Payment guide", "/budget": "Budget", "/investments": "Investments",
  "/stocks": "Investment research", "/subscriptions": "Subscriptions", "/review": "Monthly review",
  "/battle": "Spending challenges", "/recommendations": "Recommendations", "/goals": "Goals", "/settings": "Settings",
};
const primaryLinks = [
  { to: "/", label: "Calendar", icon: CalendarDays },
  { to: "/spending", label: "Spending", icon: ChartNoAxesColumnIncreasing },
  { to: "/debts", label: "Debt", icon: Landmark },
  { to: "/net-worth", label: "Net worth", icon: Layers3 },
  { to: "/agent", label: "Daily brief", icon: ListChecks },
  { to: "/assistant", label: "Talk", icon: MessageCircle },
];

export function MoneyLayout() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [more, setMore] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const previousPath = useRef(location.pathname);
  const { sync, isSyncing, error } = useTriggerSync();

  useEffect(() => {
    document.title = `${titles[location.pathname] ?? "Finances"} · Findash`;
    setMore(false);
    if (previousPath.current !== location.pathname) {
      mainRef.current?.focus({ preventScroll: true });
      if (navigationType !== "POP") window.scrollTo({ top: 0, behavior: "instant" });
    }
    previousPath.current = location.pathname;
  }, [location.pathname, navigationType]);

  useEffect(() => {
    if (!more) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setMore(false);
    };
    const dismissWithEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setMore(false);
      moreButtonRef.current?.focus();
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissWithEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissWithEscape);
    };
  }, [more]);

  return (
    <div className="money-shell">
      <a className="money-skip-link" href="#main-content">Skip to content</a>
      <header className="money-nav">
        <Link to="/" className="money-brand" aria-label="Findash home"><span aria-hidden="true">f.</span>findash</Link>
        <div className="money-nav-actions">
          <button type="button" disabled={isSyncing} onClick={() => { void sync().catch(() => {}); }} aria-label={isSyncing ? "Refreshing financial data" : "Refresh financial data"} aria-busy={isSyncing} className="money-refresh">
            <RefreshCw size={16} aria-hidden="true" className={isSyncing ? "motion-safe:animate-spin" : ""} /><span>{isSyncing ? "Refreshing…" : "Refresh"}</span>
          </button>
          <Link to="/settings" aria-label="Settings" className="money-settings-link"><Settings size={18} aria-hidden="true" /></Link>
          <div className="money-more" ref={moreRef} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setMore(false); }}>
            <button ref={moreButtonRef} type="button" aria-expanded={more} aria-controls="more-navigation" onClick={() => setMore(!more)}>More<ChevronDown size={13} aria-hidden="true" /></button>
            {more && <nav id="more-navigation" className="money-more-menu" aria-label="More pages">
              {[["/transactions", "Transactions"], ["/subscriptions", "Subscriptions"], ["/budget", "Budget"], ["/goals", "Goals"], ["/review", "Monthly review"], ["/investments", "Investments"], ["/stocks", "Investment research"], ["/recommendations", "Recommendations"], ["/settings", "Settings"]].map(([to, name]) => <NavLink key={to} to={to} onClick={() => setMore(false)}>{name}</NavLink>)}
            </nav>}
          </div>
        </div>
        <nav aria-label="Main navigation">
          {primaryLinks.map(({ to, label, icon: Icon }) => <NavLink key={to} end={to === "/"} to={to}><Icon size={17} aria-hidden="true" />{label}</NavLink>)}
        </nav>
      </header>
      <div role="status" aria-live="polite" className="sr-only">{isSyncing ? "Refreshing connected financial data." : ""}</div>
      {error && <div className="money-sync-error" role="alert"><div><strong>Financial data could not refresh.</strong><p>Existing figures are still shown. Check connection status, then try Refresh again.</p></div><Link to="/settings">Check connections</Link></div>}
      <main id="main-content" ref={mainRef} tabIndex={-1}><PageErrorBoundary key={location.pathname}><Suspense fallback={<div className="money-page" role="status"><p>Loading this view…</p></div>}><Outlet /></Suspense></PageErrorBoundary></main>
      <footer className="money-footer"><span>Findash · Private personal finance</span><span>Schedules track payments. They do not send money.</span></footer>
    </div>
  );
}
